import Layout from 'components/layout';

import React, { type FC, type PropsWithChildren } from 'react';

import { graphql, type HeadFC } from 'gatsby';
import {
  Box,
  Container,
  Heading,
  Image,
  LucidHead,
  Text,
  Link,
  LogoSection,
  StatGrid,
  PreFooterCta,
  VideoPlayer,
  VisibleVideo,
  CardCarousel,
  ProductCard,
  ABTest,
  ContentBlock,
  FeatureGrid,
} from '@lucid/components';
import { isDefined } from '@lucid/utils';
import { withImage, useSmartlingStrings } from '@lucid/hooks';

import type { Lucid } from 'types/types';

const CoHomepage: FC<PropsWithChildren<Lucid.Context.PageProps>> = ({ data, pageContext }) => {
  const { directus } = data;
  const node = directus.node;
  const { nodeTranslations } = node;
  const translatedData = node.translations?.[0] as Lucid.Directus.Generated.Co_Homepage_Translations;
  const { shortCode, urlLangPrefix, localeId, pageType, nid, shouldHideSsr, metatags, product } = pageContext;

  const {
    hero_heading,
    hero_text,
    hero_media,
    hero_link_set,
    video_section_heading,
    video_section_text,
    video_section_link,
    featured_video,
    card_carousel,
    product_section_heading,
    product_cards,
    feature_grid_items,
    feature_grid_heading,
    feature_grid_text,
    feature_highlight_heading,
    feature_highlight_text,
    feature_highlight_link,
    feature_highlight_media,
    enterprise_section_heading,
    enterprise_section_items,
    enterprise_section_image,
    enterprise_section_link,
    stat_section_heading,
    stat_blocks,
    stat_section_text,
    stat_section_link,
    logo_section,
    integration_section_heading,
    integration_section_text,
    integration_section_link,
    result_section_heading,
    result_stat_cards,
    result_section_note,
    result_section_link,
    pre_footer_heading,
    pre_footer_link,
    pre_footer_image,
  } = translatedData;

  const heroCta = hero_link_set?.[0]?.links_id;
  const cardCarouselItems = card_carousel?.items?.map(card => card.card_carousel_item_id).filter(isDefined);
  const featureGridItems = feature_grid_items?.map(fgi => fgi.feature_grid_item_id).filter(isDefined) || [];
  const productCards = product_cards?.map(card => card.basic_card_id).filter(isDefined);
  const statBlocks = stat_blocks?.map(block => block.stat_block_id).filter(isDefined);
  const logoList = logo_section?.logo_section?.logo_list
    ?.map(logo => logo.external_logo_id?.name)
    .filter(isDefined) as string[];
  const resultCards = result_stat_cards?.map(card => card.stat_block_id).filter(isDefined);

  const withSmartling = useSmartlingStrings({
    keys: ['mktg_homepage_learn_more_about', 'mktg_read_more_about'],
    shortCode,
  });

  return (
    <Layout
      localeId={localeId}
      metatags={metatags}
      nid={nid}
      nodeTranslations={nodeTranslations}
      pageType={pageType}
      product={product}
      shortCode={shortCode}
      shouldHideSsr={shouldHideSsr}
      urlLangPrefix={urlLangPrefix}
    >
      <ABTest
        isAboveTheFold
        branchesToRender={['T_A', 'T_B']}
        testName="FY25Q4_CoHomepageCtaCopy"
        willTriggerFire={nid === '1' && shortCode === 'en'}
      >
        {hero_heading && (
          <Box
            css={theme => ({
              backgroundColor: theme.colors.coolGrey[100],
              marginBottom: '-22%',
              paddingTop: theme.space.s48,
              [theme.breakpoints.bp768.min]: {
                paddingTop: theme.space.s104,
              },
            })}
          >
            <Container>
              <Heading
                as="h1"
                css={theme => ({
                  color: theme.colors.white,
                  maxWidth: '820px',
                  width: '100%',
                  marginBottom: theme.space.s16,
                  [theme.breakpoints.bp768.min]: {
                    marginBottom: theme.space.s32,
                  },
                  [theme.breakpoints.bp1220.min]: {
                    marginBottom: theme.space.s40,
                  },
                })}
                highlightTextColor="blue.60"
                text={hero_heading}
                variant="hero5XL900"
              />
              <Text
                css={theme => ({
                  color: theme.colors.white,
                  maxWidth: '480px',
                  marginBottom: theme.space.s16,
                  [theme.breakpoints.bp768.min]: {
                    marginBottom: theme.space.s32,
                  },
                  [theme.breakpoints.bp1220.min]: {
                    marginBottom: theme.space.s40,
                  },
                })}
                variant="paragraphL400"
              >
                {hero_text}
              </Text>
              <Link
                displayAsButton
                css={theme => ({
                  marginRight: theme.space.s24,
                  marginBottom: theme.space.s32,
                  [theme.breakpoints.bp768.min]: {
                    marginBottom: theme.space.s64,
                  },
                  [theme.breakpoints.bp1220.min]: {
                    marginBottom: theme.space.s80,
                  },
                })}
                href={heroCta?.final_url}
                variant="secondary"
              >
                <ABTest.Branch as="span" branch="T_A">
                  {heroCta?.default_link_text}
                </ABTest.Branch>
                <ABTest.Branch as="span" branch="T_B">
                  Request a free demo
                </ABTest.Branch>
              </Link>
              <VisibleVideo
                shouldLoop
                css={theme => ({
                  boxShadow: 'none',
                  flex: '0 1 100%',
                  margin: 'auto',
                  marginBottom: theme.space.s32,
                  minHeight: '45vw',
                  maxHeight: '678px',
                  '& video': {
                    borderRadius: theme.radii.r24,
                  },
                  [theme.breakpoints.bp900.min]: {
                    flex: '0 1 65%',
                    marginBottom: theme.space.s0,
                  },
                })}
                description={hero_media?.translations?.[0]?.alt}
                src={withImage(hero_media)}
              />
            </Container>
          </Box>
        )}
        {featured_video?.external_url && (
          <Box css={theme => ({ paddingTop: theme.space.s8, paddingBottom: theme.space.s64 })}>
            {hero_heading && (
              <Image
                css={{ width: '100%' }}
                imageAlt=""
                imageSrc="https://cdn-cashy-static-assets.lucidchart.com/marketing/pages/home/H2-2023/Hero-arc-gradient.png"
              />
            )}
            <Container extraPadding={3}>
              <Heading
                as="h2"
                css={theme => ({
                  maxWidth: '780px',
                  margin: `${theme.space.s40}px auto`,
                  marginTop: theme.space.s120,
                  width: '100%',
                  textAlign: 'center',
                })}
                highlightTextColor="blue.60"
                text={video_section_heading}
                variant="hero3XL700"
              />
              <Text
                css={theme => ({
                  maxWidth: '780px',
                  margin: `${theme.space.s40}px auto`,
                  width: '100%',
                  textAlign: 'center',
                })}
                variant="paragraphM400"
              >
                {video_section_text}
              </Text>
              <Box css={{ maxWidth: '980px', margin: 'auto' }}>
                <VideoPlayer
                  controls
                  css={theme => ({
                    flex: '0 1 100%',
                    margin: 'auto',
                    marginBottom: theme.space.s32,
                    borderRadius: theme.radii.r16,
                    [theme.breakpoints.bp900.min]: {
                      flex: '0 1 65%',
                      marginBottom: theme.space.s0,
                    },
                  })}
                  description={featured_video?.alt_text}
                  descriptionElementId="video-section-description"
                  ratio="wide"
                  url={featured_video?.external_url}
                />
                <Text
                  css={theme => ({
                    margin: `${theme.space.s40}px auto`,
                  })}
                  variant="paragraphM400"
                >
                  {featured_video?.alt_text}
                </Text>
                <ABTest.Context.Consumer>
                  {({ branch }) => (
                    <Box
                      css={[
                        { display: 'flex', justifyContent: 'end' },
                        // TODO: Remove this when the ABTest is removed
                        branch === 'T_B' && { justifyContent: 'center' },
                      ]}
                    >
                      <Link
                        displayAsButton
                        css={theme => ({ marginRight: theme.space.s24 })}
                        href={video_section_link?.final_url}
                        variant="primary"
                      >
                        {video_section_link?.default_link_text}
                      </Link>
                    </Box>
                  )}
                </ABTest.Context.Consumer>
              </Box>
            </Container>
          </Box>
        )}
        {!!cardCarouselItems?.length && card_carousel?.heading && (
          <CardCarousel
            backgroundColor="coolGrey.5"
            cardCarouselItems={cardCarouselItems}
            cardCarouselSectionHeading={card_carousel?.heading}
          />
        )}
        {product_section_heading && (
          <Box
            css={theme => ({
              backgroundColor: theme.colors.coolGrey[5],
            })}
          >
            <Container>
              <Heading
                as="h2"
                css={theme => ({
                  width: '100%',
                  textAlign: 'center',
                  margin: '0 auto',
                  padding: `${theme.space.s64}px 0`,
                  maxWidth: '580px',
                })}
                highlightTextColor="blue.60"
                text={product_section_heading}
                variant="hero3XL700"
              />
              <Box
                css={theme => [
                  {
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    paddingBottom: theme.space.s64,
                    rowGap: theme.space.s40,
                    alignItems: 'center',
                    [theme.breakpoints.bp1024.min]: {
                      display: 'none',
                    },
                  },
                  featureGridItems.length && {
                    marginBottom: `-${theme.space.s120}px`,
                    paddingBottom: 0,
                  },
                ]}
              >
                {productCards?.map(({ heading, text, link, image }, index) => (
                  <ProductCard
                    key={`productCard-${index}`}
                    heading={heading}
                    link={{
                      default_link_text: link?.default_link_text,
                      final_url: link?.final_url,
                      accessibleDescription: withSmartling('mktg_read_more_about', {
                        title: heading || '',
                      }),
                    }}
                    media={{ image }}
                    text={{ value: text }}
                  />
                ))}
              </Box>
              <Box
                css={theme => [
                  {
                    display: 'none',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    rowGap: theme.space.s40,
                    paddingBottom: theme.space.s64,
                    alignItems: 'center',
                    [theme.breakpoints.bp1024.min]: {
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'stretch',
                      columnGap: theme.space.s40,
                    },
                  },
                  featureGridItems.length && {
                    marginBottom: `-${theme.space.s120}px`,
                    paddingBottom: 0,
                  },
                ]}
              >
                {productCards?.map(({ heading, text, image, link }, index) => (
                  <ProductCard
                    key={`productCard-${index}`}
                    heading={heading}
                    link={{
                      default_link_text: link?.default_link_text,
                      final_url: link?.final_url,
                      accessibleDescription: withSmartling('mktg_read_more_about', {
                        title: heading || '',
                      }),
                    }}
                    media={{ image }}
                    text={{ value: text }}
                  />
                ))}
              </Box>
            </Container>
          </Box>
        )}
        {!!featureGridItems.length && (
          <Box
            css={theme => ({
              backgroundColor: theme.colors.indigo[10],
              padding: `240px 0px ${theme.space.s104}px`,
            })}
          >
            <Container>
              <FeatureGrid
                items={featureGridItems}
                sectionSubtitle={feature_grid_text}
                sectionTitle={feature_grid_heading}
                titleColor="coolGrey.100"
                titleTextVariant="headingXL"
                variant="resources"
              />
            </Container>
          </Box>
        )}
        {enterprise_section_heading && (
          <Box
            css={theme => ({
              background: `linear-gradient(180deg, ${theme.colors.blue[90]} 0%, ${theme.colors.blue[100]} 100%)`,
              padding: theme.space.s64,
            })}
          >
            <Container>
              <Heading
                as="h2"
                css={theme => ({
                  color: theme.colors.white,
                  width: '100%',
                  textAlign: 'center',
                  marginBottom: theme.space.s64,
                })}
                highlightTextColor="teal.20"
                text={enterprise_section_heading}
                variant="hero3XL700"
              />
              <Box
                css={theme => ({
                  display: 'grid',
                  gridTemplateAreas: '"image" "text"',
                  justifyItems: 'center',
                  rowGap: theme.space.s64,
                  [theme.breakpoints.bp1220.min]: {
                    gridTemplateAreas: '"text image"',
                    gridTemplateColumns: '1fr 550px',
                    columnGap: theme.space.s64,
                  },
                })}
              >
                <Box css={{ gridArea: 'text' }}>
                  {enterprise_section_items?.map(
                    ({ text }: Lucid.Directus.Repeaters.Enterprise_Section_Bullets, index: number) => {
                      const isNotLastItem = enterprise_section_items?.length !== index + 1;

                      return (
                        <React.Fragment key={`enterpriseSection-item-${index}`}>
                          <Text
                            css={theme => ({
                              color: 'white',
                              marginBottom: isNotLastItem ? theme.space.s24 : 0,
                              '.colorizedText': {
                                fontWeight: theme.fontWeights.fw700,
                              },
                            })}
                            highlightTextColor="teal.20"
                            text={text}
                            variant="paragraphL400"
                          />
                          {isNotLastItem && (
                            <Box
                              css={theme => ({
                                height: '1px',
                                borderBottom: '1px solid white',
                                marginBottom: theme.space.s16,
                              })}
                            />
                          )}
                        </React.Fragment>
                      );
                    },
                  )}
                </Box>
                <Image
                  css={{ width: '550px', gridArea: 'image' }}
                  imageAlt={enterprise_section_image?.translations?.[0]?.alt || ''}
                  imageSrc={withImage(enterprise_section_image)}
                />
              </Box>
              <Link
                aria-label={withSmartling('mktg_homepage_learn_more_about', {
                  title: enterprise_section_heading.replace(/\{\{|\}\}/g, ''),
                })}
                css={theme => ({ marginTop: theme.space.s40, fontSize: '20px' })}
                href={enterprise_section_link?.final_url}
                iconAnimation="navigational"
                variant="primaryInverse"
              >
                {enterprise_section_link?.default_link_text}
              </Link>
            </Container>
          </Box>
        )}
        {!!statBlocks?.length && (
          <StatGrid
            heading={stat_section_heading}
            headingHighlightColor="blue.60"
            items={statBlocks}
            languageKey={shortCode}
            link={stat_section_link}
            statColor="blue.60"
            text={stat_section_text}
            variant="divided"
          />
        )}
        {!!logoList?.length && (
          <Box
            css={theme => [
              {
                width: '100%',
                overflow: 'hidden',
                [theme.breakpoints.bp900.min]: {
                  marginTop: theme.space.s64,
                },
              },
            ]}
          >
            <LogoSection height={40} logos={logoList} logosColor="coolGrey" variant="carousel" />
          </Box>
        )}
        {feature_highlight_heading && (
          <ContentBlock
            backgroundColor={'coolGrey.5'}
            css={theme => ({
              [theme.breakpoints.bp900.min]: {
                paddingTop: theme.space.s120,
                paddingBottom: theme.space.s120,
              },
            })}
            heading={feature_highlight_heading}
            link={feature_highlight_link}
            media={feature_highlight_media}
            mediaPosition={['bottom', 'left']}
            text={feature_highlight_text}
          />
        )}
        {integration_section_heading && (
          <Box
            css={theme => ({
              background: `radial-gradient(300.75% 237.68% at 26.19% 7.43%, ${theme.colors.blue[60]} 0%, ${theme.colors.pink[60]} 48.52%, ${theme.colors.indigo[60]} 96.88%)`,
            })}
          >
            <Box
              css={theme => ({
                padding: `${theme.space.s80}px ${theme.space.s24}px 0 ${theme.space.s24}px`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                [theme.breakpoints.bp768.min]: {
                  padding: `230px ${theme.space.s24}px`,
                  paddingBottom: 0,
                },
              })}
            >
              <Heading
                as="h2"
                css={theme => ({
                  color: theme.colors.white,
                  maxWidth: '780px',
                  textAlign: 'center',
                })}
                variant="hero3XL700"
              >
                {integration_section_heading}
              </Heading>
              <Text
                css={theme => ({
                  color: theme.colors.white,
                  maxWidth: '780px',
                  textAlign: 'center',
                  [theme.breakpoints.bp500.min]: {
                    textAlign: 'left',
                  },
                })}
                variant="paragraphM400"
              >
                {integration_section_text}
              </Text>
              <Box
                css={theme => ({
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  maxWidth: '780px',
                  padding: `${theme.space.s40}px 0`,
                  paddingBottom: 0,
                  zIndex: 5,
                  [theme.breakpoints.bp500.min]: {
                    justifyContent: 'end',
                  },
                })}
              >
                <Link
                  css={{ fontSize: '20px' }}
                  href={integration_section_link?.final_url}
                  iconAnimation="navigational"
                  variant="primaryInverse"
                >
                  {integration_section_link?.default_link_text}
                </Link>
              </Box>
            </Box>
            <Container
              css={theme => ({
                display: 'none',
                [theme.breakpoints.bp1024.min]: {
                  display: 'block',
                },
              })}
            >
              <Image
                css={{
                  marginTop: '-30%',
                  marginBottom: '-4px',
                  width: '100%',
                  mixBlendMode: 'multiply',
                }}
                imageAlt=""
                imageSrc="https://cdn-cashy-static-assets.lucidchart.com/marketing/pages/home/H2-2023/integration-logos-01.png"
              />
            </Container>
            <Box
              css={theme => ({
                marginTop: '-30%',
                marginBottom: '-4px',
                width: '100%',
                mixBlendMode: 'multiply',
                [theme.breakpoints.bp1024.min]: {
                  display: 'none',
                },
                display: 'block',
              })}
            >
              <Image
                imageAlt=""
                imageSrc="https://cdn-cashy-static-assets.lucidchart.com/marketing/pages/home/H2-2023/integration-logos-01.png"
              />
            </Box>
          </Box>
        )}
        {!!resultCards?.length && (
          <StatGrid
            css={theme => ({
              background: `linear-gradient(140.24deg, ${theme.colors.coolGrey[100]} 5.93%, ${theme.colors.blue[100]} 78.67%)`,
            })}
            heading={result_section_heading}
            headingHighlightColor="teal.20"
            items={resultCards}
            languageKey={shortCode}
            link={result_section_link}
            note={result_section_note}
            statColor="blue.60"
            variant="cards"
          />
        )}
        {pre_footer_link?.final_url && (
          <ABTest.Context.Consumer>
            {({ branch }) => (
              <PreFooterCta
                hasGradient
                backgroundColor="coolGrey.5"
                ctas={
                  branch === 'T_B'
                    ? [{ ...pre_footer_link, default_link_text: 'Request a free demo' }]
                    : [pre_footer_link]
                }
                heading={pre_footer_heading}
                headingVariant="hero3XL700"
                image={pre_footer_image}
              />
            )}
          </ABTest.Context.Consumer>
        )}
      </ABTest>
    </Layout>
  );
};

export default CoHomepage;

export const Head: HeadFC<Lucid.Context.HeadData, Lucid.Context.HeadContext> = ({ ...props }) => (
  <LucidHead {...props} />
);

export const query = graphql`
  query CoHomepageQuery($nid: ID!, $shortCode: String!) {
    directus {
      node: co_homepage_by_id(id: $nid) {
        nodeTranslations: translations {
          is_published
          languages_code {
            short_code
          }
          path {
            url
          }
        }
        translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
          hero_heading
          hero_text
          hero_media {
            ...mediaQuery
          }
          hero_link_set {
            links_id {
              final_url
              default_link_text
            }
          }
          video_section_heading
          video_section_text
          featured_video {
            external_url
            title
            preview_image {
              ...mediaQuery
            }
            translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
              title
              alt_description
            }
            alt_text
          }
          video_section_link {
            final_url
            default_link_text
          }
          card_carousel {
            heading
            items {
              card_carousel_item_id {
                heading
                text
                bullet_points
                link {
                  default_link_text
                  final_url
                }
                media {
                  ...mediaQuery
                }
                integration_icons {
                  directus_files_id {
                    ...mediaQuery
                  }
                }
              }
            }
          }
          feature_grid_heading
          feature_grid_text
          feature_grid_items {
            feature_grid_item_id {
              heading
              html
              image {
                ...mediaQuery
              }
              link {
                beacon_id
                default_link_text
                final_url
              }
            }
          }
          product_section_heading
          product_cards {
            basic_card_id {
              id
              heading
              text
              image {
                ...mediaQuery
              }
              link {
                final_url
                default_link_text
              }
            }
          }
          feature_highlight_heading
          feature_highlight_text
          feature_highlight_link {
            default_link_text
            final_url
          }
          feature_highlight_media {
            ...mediaQuery
          }
          enterprise_section_heading
          enterprise_section_items
          enterprise_section_image {
            ...mediaQuery
          }
          enterprise_section_link {
            final_url
            default_link_text
          }
          stat_section_heading
          stat_section_text
          stat_section_link {
            final_url
            default_link_text
          }
          stat_blocks {
            stat_block_id {
              id
              heading
              text
            }
          }
          logo_section {
            heading
            link {
              default_link_text
              final_url
            }
            logo_section {
              logo_list {
                external_logo_id {
                  name
                }
              }
            }
          }
          integration_section_heading
          integration_section_text
          integration_section_link {
            default_link_text
            final_url
          }
          result_section_heading
          result_section_note
          result_section_link {
            final_url
            default_link_text
          }
          result_stat_cards {
            stat_block_id {
              id
              heading
              text
              image {
                ...mediaQuery
              }
            }
          }
          pre_footer_heading
          pre_footer_link {
            final_url
            default_link_text
          }
          pre_footer_image {
            ...mediaQuery
          }
        }
      }
    }
  }
`;
